import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { getToken } from '../api.js';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

const FONT_SIZES = ['12px', '14px', '16px', '18px', '20px', '24px', '32px'];
const FONTS = [
  { label: '기본', value: '' },
  { label: 'Pretendard', value: 'Pretendard, sans-serif' },
  { label: '맑은 고딕', value: '"Malgun Gothic", "Apple SD Gothic Neo", sans-serif' },
  { label: '나눔고딕', value: '"Nanum Gothic", sans-serif' },
  { label: '바탕', value: '"Batang", serif' },
  { label: 'Serif', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Mono', value: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
];

function MenuBar({ editor, onInsertImage, uploading }) {
  if (!editor || typeof editor.chain !== 'function' || typeof editor.isActive !== 'function') return null;
  const isActive = (...args) => { try { return editor.isActive(...args); } catch { return false; } };
  const Btn = ({ on, active, children, title }) => (
    <button type="button" title={title} onClick={on}
      className={'h-8 min-w-[2rem] px-2 rounded text-sm hover:bg-medical-50 ' + (active ? 'bg-medical-100 text-medical-800 font-semibold' : 'text-medical-700')}>
      {children}
    </button>
  );

  const setFontSize = (size) => {
    if (!size) editor.chain().focus().unsetMark('textStyle').run();
    else editor.chain().focus().setMark('textStyle', { fontSize: size }).run();
  };
  const setFontFamily = (val) => {
    if (!val) editor.chain().focus().unsetMark('textStyle').run();
    else editor.chain().focus().setMark('textStyle', { fontFamily: val }).run();
  };

  const insertLink = () => {
    const url = window.prompt('링크 URL을 입력하세요', 'https://');
    if (url) editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-medical-100 bg-medical-50/40 sticky top-0">
      <select className="h-8 text-sm bg-white border border-medical-200 rounded px-1"
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'p') editor.chain().focus().setParagraph().run();
          else editor.chain().focus().toggleHeading({ level: Number(v) }).run();
        }}>
        <option value="p">본문</option>
        <option value="1">제목 1</option>
        <option value="2">제목 2</option>
        <option value="3">제목 3</option>
      </select>
      <select className="h-8 text-sm bg-white border border-medical-200 rounded px-1" defaultValue=""
        onChange={(e) => setFontFamily(e.target.value)}>
        {FONTS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
      </select>
      <select className="h-8 text-sm bg-white border border-medical-200 rounded px-1" defaultValue=""
        onChange={(e) => setFontSize(e.target.value)}>
        <option value="">크기</option>
        {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
      <Btn title="굵게" on={() => editor.chain().focus().toggleBold().run()} active={isActive('bold')}><b>B</b></Btn>
      <Btn title="기울임" on={() => editor.chain().focus().toggleItalic().run()} active={isActive('italic')}><i>I</i></Btn>
      <Btn title="밑줄" on={() => editor.chain().focus().toggleUnderline().run()} active={isActive('underline')}><u>U</u></Btn>
      <Btn title="취소선" on={() => editor.chain().focus().toggleStrike().run()} active={isActive('strike')}><s>S</s></Btn>
      <input type="color" title="글자색" className="h-8 w-8 cursor-pointer rounded border border-medical-200 bg-white"
        onChange={(e) => editor.chain().focus().setColor(e.target.value).run()} />
      <Btn title="왼쪽 정렬" on={() => editor.chain().focus().setTextAlign('left').run()}>≡</Btn>
      <Btn title="가운데 정렬" on={() => editor.chain().focus().setTextAlign('center').run()}>≡</Btn>
      <Btn title="오른쪽 정렬" on={() => editor.chain().focus().setTextAlign('right').run()}>≡</Btn>
      <Btn title="글머리표" on={() => editor.chain().focus().toggleBulletList().run()} active={isActive('bulletList')}>•</Btn>
      <Btn title="번호목록" on={() => editor.chain().focus().toggleOrderedList().run()} active={isActive('orderedList')}>1.</Btn>
      <Btn title="인용" on={() => editor.chain().focus().toggleBlockquote().run()} active={isActive('blockquote')}>“”</Btn>
      <Btn title="코드" on={() => editor.chain().focus().toggleCode().run()} active={isActive('code')}>{'<>'}</Btn>
      <Btn title="링크" on={insertLink} active={isActive('link')}>🔗</Btn>
      <Btn title={uploading ? '업로드 중...' : '이미지 업로드'} on={onInsertImage}>{uploading ? '⏳' : '🖼'}</Btn>
      <Btn title="실행취소" on={() => editor.chain().focus().undo().run()}>↶</Btn>
      <Btn title="다시실행" on={() => editor.chain().focus().redo().run()}>↷</Btn>
    </div>
  );
}

const FontSize = TextStyle.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fontSize: {
        default: null,
        parseHTML: el => el.style.fontSize || null,
        renderHTML: attrs => attrs.fontSize ? { style: `font-size:${attrs.fontSize}` } : {},
      },
      fontFamily: {
        default: null,
        parseHTML: el => el.style.fontFamily || null,
        renderHTML: attrs => attrs.fontFamily ? { style: `font-family:${attrs.fontFamily}` } : {},
      },
    };
  },
});

export default function RichEditor({ value, onChange, placeholder = '내용을 입력하세요…' }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      FontSize,
      Color,
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      try {
        if (typeof onChange === 'function' && ed && typeof ed.getHTML === 'function') {
          onChange(ed.getHTML());
        }
      } catch (err) {
        console.warn('[RichEditor] onUpdate error:', err?.message || err);
      }
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    try {
      if (typeof editor.getHTML !== 'function') return;
      const target = value || '';
      const current = editor.getHTML();
      if (target === current) return;
      if (target === '' && current === '<p></p>') return;
      editor.commands?.setContent?.(target, false);
    } catch (err) {
      console.warn('[RichEditor] content sync skipped:', err?.message || err);
    }
  }, [value, editor]);

  const uploadImageFile = async (file) => {
    if (!file || !editor) return;
    if (!file.type?.startsWith('image/')) { alert('이미지 파일만 업로드할 수 있습니다.'); return; }
    if (file.size > 10 * 1024 * 1024) { alert('이미지 크기는 10MB 이하여야 합니다.'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch('/api/inline-images', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + (getToken() || '') },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '업로드 실패');
      editor.chain().focus().setImage({ src: data.url, alt: file.name }).run();
    } catch (err) {
      alert(err.message || '이미지 업로드 실패');
    } finally {
      setUploading(false);
    }
  };

  const onImageButtonClick = () => fileInputRef.current?.click();
  const onImagePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    uploadImageFile(f);
  };
  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const it of items) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        e.preventDefault();
        uploadImageFile(it.getAsFile());
        return;
      }
    }
  };
  const onDrop = (e) => {
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;
    const imgs = Array.from(files).filter(x => x.type.startsWith('image/'));
    if (imgs.length > 0) { e.preventDefault(); imgs.forEach(uploadImageFile); }
  };

  if (!editor) {
    return <div className="border border-medical-200 rounded-lg bg-white p-4 text-sm text-medical-400">에디터 로딩 중...</div>;
  }

  return (
    <div className="border border-medical-200 rounded-lg bg-white overflow-hidden">
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onImagePicked} />
      <MenuBar editor={editor} onInsertImage={onImageButtonClick} uploading={uploading} />
      <div onPaste={onPaste} onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
